import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Confirmação exigida antes de salvar uma chamada em que ninguém faltou.
 *
 * O formulário de frequência já nasce com a turma inteira marcada como
 * "presente", então salvar sem conferir é fácil — e o resultado no banco
 * fica idêntico ao de uma aula em que realmente ninguém faltou. Sem essa
 * distinção, a consolidação do dia (backend/api/attendance_sync.py) não tem
 * como confiar numa chamada sem faltas, e ela deixa de valer sobre uma falta
 * lançada na chamada do dia.
 *
 * Confirmando aqui, o lançamento passa a ser uma afirmação do professor e o
 * backend registra isso na aula (`all_present_confirmed`).
 */
function ConfirmarTodosPresentesModal({
  aberto,
  totalAlunos,
  contexto,
  salvando = false,
  onConfirmar,
  onRevisar,
}) {
  const { darkMode } = useTheme();

  if (!aberto) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmar-todos-presentes-titulo"
    >
      <div className={`w-full max-w-md rounded-2xl p-6 ${darkMode
        ? 'bg-slate-800 border border-slate-700'
        : 'bg-white shadow-xl'
      }`}>
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl leading-none">🖐️</div>
          <h2
            id="confirmar-todos-presentes-titulo"
            className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}
          >
            Ninguém faltou{contexto ? ` ${contexto}` : ''}?
          </h2>
        </div>

        <p className={`text-sm mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          {totalAlunos === 1 ? (
            <>O único aluno da turma está marcado como presente.</>
          ) : (
            <>
              Os <strong>{totalAlunos}</strong> alunos da turma estão marcados
              como presentes.
            </>
          )}
        </p>
        <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          A chamada começa com todos presentes, então vale conferir antes de
          salvar. Se alguém faltou e ficar marcado presente, a falta some do
          boletim do aluno.
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            onClick={() => onRevisar()}
            disabled={salvando}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            Revisar a chamada
          </button>
          <button
            onClick={() => onConfirmar()}
            disabled={salvando}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${
              salvando
                ? 'bg-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
            }`}
          >
            {salvando ? 'Salvando...' : 'Confirmo, ninguém faltou'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmarTodosPresentesModal;
